/* eslint-disable no-nested-ternary */
import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import LayoutContext from "../context/layout-context";
import Button from "../components/button/Button";
import {
  AgentCreate,
  AgentCreations,
  DestinationAccounts
} from "../utils/helpers/types";
import { initialPaginationData } from "../utils/constants";
import styles from "./self-service.module.scss";
import {
  createAgent,
  createDestinationAccount,
  getAgents,
  updateAgent
} from "../utils/helpers/data/super-agent";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import Table, { Column } from "../components/table/Table";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import style from "./history.module.scss";
import Input from "../components/input/Input";

const SuperAgent: FunctionComponent<RouteComponentProps> = () => {
  let timerRef: NodeJS.Timeout;
  const { notify } = useContext(LayoutContext);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [agentOnEdit, setAgentOnEdit] = useState<AgentCreations | null>(null);
  const [addAccount, setAddAccount] = useState<DestinationAccounts | null>(
    null
  );
  const [agentOnView, setAgentOnView] = useState<AgentCreations | null>(null);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [agentCreate, setAgentCreate] = useState<AgentCreations[]>([]);
  const [agents, setAgents] = useState<AgentCreations[]>([]);
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);

  const [appId, setAppId] = useState<string>("");
  const [institutionId, setInstitutionId] = useState<string>("");
  const [merchantCode, setMerchantCode] = useState<string>("");
  const [acquirer, setAcquirer] = useState<string>("");

  const showResetFilter = appId || institutionId || merchantCode || acquirer;

  const resetFilters = () => {
    setAppId("");
    setInstitutionId("");
    setMerchantCode("");
    setAcquirer("");
    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const displayAddEditModal = (agentCreate: AgentCreations | null) => {
    setAgentOnEdit(agentCreate);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setAgentOnEdit(null);
    setShowAddEditModal(false);
  };

  const [showAddDestinationModal, setShowAddDestinationModal] = useState(false);

  const displayAddDestinationModal = (agent: DestinationAccounts) => {
    setShowAddDestinationModal(true);

    setAddAccount({
      institutionId: agent.institutionId || "",
      appId: agent.appId || 0
    });
  };

  const fetchAgents = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getAgents(
      pageNumber,
      pageSize,
      appId,
      institutionId,
      merchantCode,
      acquirer
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch agents: ${message}`);
      return;
    }

    setAgents(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });
    setReset(false);
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchAgents(pagination.pageNumber, pagination.pageSize);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching map acquirer.", error);
    }
  };

  useEffect(() => {
    if (!reset) {
      fetchAgents();
    } else {
      // Reset logic
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const columns: Column<AgentCreations>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Institution",
      dataIndex: "institutionId",
      key: "institutionId"
    },
    {
      title: "App Id",
      dataIndex: "appId",
      key: "appId"
    },
    {
      title: "Acquirer",
      dataIndex: "acquirer",
      key: "acquirer"
    },
    {
      title: "Mechant code",
      dataIndex: "merchantDomainCode",
      key: "merchantDomainCode"
    },

    {
      title: "",
      render: (_: unknown, agent: AgentCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              setAgentOnView(agent);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(agent);
            }
          },
          {
            label: "Add destination account",
            onClick: () => {
              displayAddDestinationModal(agent);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">
        Super Agent Batch Transactions
      </h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={appId}
                  onChange={(value: string) => setAppId(value)}
                  placeholder="Enter appId"
                />
              </div>

              <div className="input-group half-width">
                <Input
                  value={institutionId}
                  onChange={(value: string) => setInstitutionId(value)}
                  placeholder="Enter InstitutionId"
                />
              </div>

              <div className="input-group half-width">
                <Input
                  value={merchantCode}
                  onChange={(value: string) => setMerchantCode(value)}
                  placeholder="Enter MerchantCode"
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>
          Create Super Agent
        </Button>
      </div>

      <Table
        dataSource={agents || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchAgents}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <AgentViewModal
        agent={agentOnView}
        cancel={() => setAgentOnView(null)}
        refresh={fetchAgents}
        visible={Boolean(agentOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        agentOnEdit={agentOnEdit}
        cancel={dismissAddEditModal}
        refresh={stayOnPage =>
          fetchAgents(stayOnPage ? pagination.pageNumber : 1)
        }
        agentCreation={agentCreate}
      />

      <AddDestinationModal
        visible={showAddDestinationModal}
        cancel={() => setShowAddDestinationModal(false)}
        refresh={fetchAgents}
        institutionId={addAccount ? addAccount.institutionId || "" : ""}
        appId={addAccount ? addAccount.appId || 0 : 0}
      />
    </main>
  );
};

const blankAgent: AgentCreate = {
  name: "",
  creditIntervalInMin: 0,
  institutionId: "",
  appId: 0,
  acquirer: "",
  acquirerCode: "",
  merchantDomainCode: "",
  batchTransactions: false,
  destinationAccounts: []
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    agentOnEdit: AgentCreations | null;
    refresh: (stayOnPage: boolean) => void;
    agentCreation: AgentCreations[];
    cancel: () => void;
  }
> = ({ visible, cancel, agentOnEdit, refresh, agentCreation }) => {
  const [formData, setFormData] = useState<AgentCreate>({
    ...blankAgent,
    ...agentOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (
    key: keyof AgentCreate,
    value: string | number | boolean | string[]
  ) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [key]:
        key === "destinationAccounts"
          ? Array.isArray(value)
            ? value
            : [String(value)]
          : value
    }));
  };

  useEffect(() => {
    setFormData(
      agentOnEdit ? { ...blankAgent, ...agentOnEdit } : { ...blankAgent }
    );
  }, [visible, agentOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    let id: number | undefined;

    if (agentOnEdit && typeof agentOnEdit.id === "number") {
      id = agentOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message, data } = await updateAgent(id, {
        ...formData
      });

      if (error) {
        // eslint-disable-next-line no-useless-escape
        const errorMessage = (message ?? "").replace(/[\]\[]/g, "");
        notify("error", `Unable to update agent: ${errorMessage}}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated Agent");
    } else {
      const { error, message, data } = await createAgent({
        ...formData
      });

      if (error) {
        // eslint-disable-next-line no-useless-escape
        const errorMessage = (message ?? "").replace(/[\]\[]/g, "");
        notify("error", `Unable to create agent: ${errorMessage}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created Super Agent");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!isCreateOperation && agentOnEdit) {
      setFormData({
        name: agentOnEdit.name,
        creditIntervalInMin: agentOnEdit.creditIntervalInMin,
        institutionId: agentOnEdit.institutionId || "",
        appId: agentOnEdit.appId,
        acquirer: agentOnEdit.acquirer || "",
        acquirerCode: agentOnEdit.acquirerCode || "",
        merchantDomainCode: agentOnEdit.merchantDomainCode || "",
        batchTransactions: agentOnEdit.batchTransactions || false,
        destinationAccounts: agentOnEdit.destinationAccounts || [""]
      });
    }
  }, [visible, agentOnEdit]);

  const isCreateOperation = agentOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Name</span>
          <Input
            value={formData.name}
            onChange={value => handleChange("name", value)}
            placeholder="Enter agent name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Credit Interval in Min</span>
          </div>
          <Input
            value={formData.creditIntervalInMin}
            onChange={value =>
              handleChange("creditIntervalInMin", Number(value))
            }
            placeholder="Enter creditIntervalInMin"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Institution Id</span>
          </div>
          <Input
            value={formData.institutionId}
            onChange={value => handleChange("institutionId", value)}
            placeholder="Enter institutionId"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">App Id</span>
          <Input
            value={formData.appId}
            onChange={value => handleChange("appId", Number(value))}
            placeholder="Enter appId"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Acquirer</span>
          <Input
            value={formData.acquirer}
            onChange={value => handleChange("acquirer", value)}
            placeholder="Enter acquirer"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Acquirer Code</span>
          <Input
            value={formData.acquirerCode}
            onChange={value => handleChange("acquirerCode", value)}
            placeholder="Enter acquirer code"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Merchant DomainCode</span>
          <Input
            value={formData.merchantDomainCode}
            onChange={value => handleChange("merchantDomainCode", value)}
            placeholder="Enter Merchant DomainCode"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Batch Transactions</span>
          <Input
            value={formData.batchTransactions.toString()}
            onChange={value => handleChange("batchTransactions", value)}
            placeholder="Enter batch transactions"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Destination account</span>
          <Input
            value={formData.destinationAccounts.toString()}
            onChange={value => handleChange("destinationAccounts", value)}
            placeholder="Enter destination account"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {agentOnEdit ? "Edit Super Agent" : "Create Super Agent"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface AgentEditModalProps {
  agent: AgentCreations | null;
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
}

const AgentViewModal: FunctionComponent<AgentEditModalProps> = ({
  agent: _agent,
  cancel,
  visible,
  refresh
}) => {
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<AgentCreations | null>(_agent);

  useEffect(() => {
    console.log("Received API response:", agents);
    setAgents(_agent);
  }, [_agent]);
  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{agents?.id || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Name:</span>
            <span className={styles.value}>{agents?.name || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Institution Id:</span>
            <span className={styles.value}>
              {agents?.institutionId || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>App Id:</span>
            <span className={styles.value}>{agents?.appId || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Acquirer:</span>
            <span className={styles.value}>{agents?.acquirer || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Acquirer Code:</span>
            <span className={styles.value}>
              {agents?.acquirerCode || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Merchant domain code:</span>
            <span className={styles.value}>
              {agents?.merchantDomainCode || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Batch transactions:</span>
            <span className={styles.value}>
              {agents?.batchTransactions !== undefined
                ? agents?.batchTransactions.toString()
                : "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Destination account:</span>
            <span className={styles.value}>
              {agents?.destinationAccounts.join(", ") || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Credit interval in min:</span>
            <span className={styles.value}>
              {agents?.creditIntervalInMin || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};

interface AddDestinationModalProps extends ModalProps {
  cancel: () => void;
  refresh: () => void;
  visible: boolean;
  institutionId: string;
  appId: number;
}

const AddDestinationModal: FunctionComponent<AddDestinationModalProps> = ({
  visible,
  cancel,
  refresh,
  institutionId,
  appId
}) => {
  const [formData, setFormData] = useState({
    appId,
    institutionId,
    destinationAccounts: [] as string[]
  });

  const [isSaving, setIsSaving] = useState(false);

  const { notify } = useContext(LayoutContext);

  const handleChange = (
    key: keyof typeof formData,
    value: string | number | string[]
  ) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [key]:
        key === "appId"
          ? Number(value)
          : key === "institutionId"
          ? String(value)
          : Array.isArray(value)
          ? value.map(String)
          : [String(value)]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    const { error, message, data } = await createDestinationAccount(formData);

    if (error) {
      console.error("Error creating destination accounts:", message);
      // eslint-disable-next-line no-useless-escape
      const errorMessage = (message ?? "").replace(/[\]\[]/g, "");
      notify("error", `Unable to add destination account: ${errorMessage}`);
    } else {
      notify?.("success", "Destination accounts created successfully");

      refresh?.();
      cancel?.();
    }

    setIsSaving(false);
  };

  useEffect(() => {
    setFormData({
      appId,
      institutionId,
      destinationAccounts: [] as string[]
    });
  }, [visible, appId, institutionId]);

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">APP Id</span>
          <Input
            value={formData.appId}
            onChange={value => handleChange("appId", value)}
            placeholder="Enter appId"
            responsive
            required
            disabled
          />
        </div>

        <div className="input-group">
          <span className="question">Institution Id</span>
          <Input
            value={formData.institutionId}
            onChange={value => handleChange("institutionId", value)}
            placeholder="Enter Institution Id"
            responsive
            required
            disabled
          />
        </div>

        <div className="input-group">
          <span className="question">Destination Accounts</span>
          <Input
            value={formData.destinationAccounts.toString()}
            onChange={value => handleChange("destinationAccounts", value)}
            placeholder="Enter destination account"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={cancel}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            Create Destination Accounts
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SuperAgent;
